import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { endOfDay, startOfDay } from 'date-fns';
import {
  Button, Checkbox, CircularProgress, Collapse, FormControlLabel, IconButton, InputAdornment, LinearProgress,
  ListItemText, MenuItem, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip
} from '@mui/material';
import {
  CheckCircle, Error as ErrorIcon, HelpOutline, KeyboardArrowDown, KeyboardArrowRight, RestartAlt, Search
} from '@mui/icons-material';
import DateRangePicker from '../../DateRangePicker';
import FormattedDateTime from '../../FormattedDateTime';
import Code from '../../input/Code';
import { loadSiemEvent, loadSiemEventFilterOptions, loadSiemEvents } from '../../../data/Siem';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;
// The backend keeps events for 90 days, so longer ranges are not offered.
const EXCLUDED_RANGES = [{ id: 'THISYEAR' }, { id: 'LASTYEAR' }, { id: 'UNRESTRICTED' }];
// Index of "Heute" in the DateRangePicker options; initialFilter uses the same range.
const DEFAULT_RANGE_INDEX = 0;
const MAX_DETAIL_LENGTH = 80;
// Additional requests per "load", when the server stopped reading before the page was full (truncated). With a filter
// each request reads up to 2.500 events in DynamoDB, so this bounds the read cost of a rarely matching filter
// (~15.000 events). Afterwards the user continues explicitly with "load more".
const MAX_AUTO_LOAD_ROUNDS = 5;
// Distance to the end of the list at which the next page is loaded while scrolling.
const SCROLL_LOAD_MARGIN = '200px';
const COLUMN_COUNT = 6;
// Every request and every record access is an event of its own, so these hide the security relevant events by default.
const DEFAULT_EXCLUDED_CATEGORIES = ['API', 'OBJECT'];
// Value of the category entries in the event select; selecting one toggles all actions of the category.
const CATEGORY_PREFIX = 'category:';

/**
 * @param actions selected action codes; null until the filter options are loaded, an empty list means all actions.
 */
function initialFilter (actions = null) {
  const now = new Date();
  return { from: startOfDay(now), to: endOfDay(now), actions, status: '', q: '' };
}

export function defaultActions (actionOptions) {
  return actionOptions.filter(option => !DEFAULT_EXCLUDED_CATEGORIES.includes(option.category)).map(option => option.code);
}

function sameActions (left, right) {
  return left.length === right.length && left.every(code => right.includes(code));
}

/**
 * New selection after a change of the event select: a category entry selects all of its actions, or deselects them
 * if all were selected already.
 */
export function toggleActions (selected, value, actionsByCategory) {
  const category = value.find(item => item.startsWith(CATEGORY_PREFIX));
  if (!category) return value;
  const codes = (actionsByCategory[category.substring(CATEGORY_PREFIX.length)] || []).map(option => option.code);
  return codes.every(code => selected.includes(code))
    ? selected.filter(code => !codes.includes(code))
    : [...selected, ...codes.filter(code => !selected.includes(code))];
}

function translateOrDefault (intl, id, fallback) {
  return intl.messages[id] ? intl.formatMessage({ id }) : fallback;
}

export function actionLabel (intl, action) {
  if (!action?.code) return '';
  return translateOrDefault(intl, `siem.events.action.${action.code}`, action.code);
}

function statusLabel (intl, event) {
  return intl.formatMessage({ id: `siem.events.status.${event.status || 'UNKNOWN'}` });
}

function shorten (value) {
  if (value === null || value === undefined || value === '') return '–';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return text.length > MAX_DETAIL_LENGTH ? text.substring(0, MAX_DETAIL_LENGTH - 1) + '…' : text;
}

/**
 * Compact, human readable summary of an event for the "Details" column.
 */
export function detailSummary (event) {
  const details = event.details || {};
  const lines = [];
  if (Array.isArray(details.valueUpdates)) {
    details.valueUpdates.slice(0, 2)
      .forEach(update => lines.push(`${update.name}: ${shorten(update.oldValue)} → ${shorten(update.newValue)}`));
    if (details.valueUpdates.length > 2) {
      lines.push(`+${details.valueUpdates.length - 2}`);
    }
  }
  if (event.failureReason) {
    lines.push(event.failureReason);
  }
  if (details.method || details.path) {
    lines.push(shorten(`${details.method || ''} ${details.path || ''}`.trim()));
  }
  if (!lines.length && details.attributes && typeof details.attributes === 'object') {
    lines.push(Object.entries(details.attributes).slice(0, 2).map(([key, value]) => `${key}: ${shorten(value)}`).join(', '));
  }
  return lines;
}

function userLabel (intl, actor) {
  return actor?.userName || actor?.userId || intl.formatMessage({ id: 'siem.events.user.system' });
}

export function resourceLabel (intl, resource) {
  if (!resource) return '–';
  const type = resource.type && translateOrDefault(intl, `siem.events.resourceType.${resource.type}`, resource.type);
  return [type, resource.name || resource.id].filter(Boolean).join(': ');
}

function StatusIcon ({ event }) {
  const intl = useIntl();
  const hint = [statusLabel(intl, event), event.statusCode, event.failureReason].filter(Boolean).join(' · ');
  let icon = <HelpOutline className='status unknown'/>;
  if (event.status === 'SUCCESS') icon = <CheckCircle className='status success'/>;
  if (event.status === 'FAILURE') icon = <ErrorIcon className='status failure'/>;
  return <Tooltip title={hint}>{icon}</Tooltip>;
}

StatusIcon.propTypes = {
  event: PropTypes.object.isRequired
};

function ValueChanges ({ updates }) {
  return (
    <table className='changes'>
      <tbody>
        {updates.map((update, index) => (
          <tr key={index}>
            <td>{update.name}</td>
            <td>{shorten(update.oldValue)}</td>
            <td>→</td>
            <td>{shorten(update.newValue)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

ValueChanges.propTypes = {
  updates: PropTypes.array.isRequired
};

/**
 * Value with secondary lines below it (e.g. e-mail and ID below the user name). Lines already contained in the value
 * are skipped, because user names often include the e-mail address.
 */
function WithSecondary ({ value, secondary }) {
  const lines = secondary.filter(line => line && !String(value).includes(line));
  return (
    <>
      {value}
      {lines.map(line => <div key={line} className='secondary'>{line}</div>)}
    </>
  );
}

WithSecondary.propTypes = {
  value: PropTypes.node,
  secondary: PropTypes.array.isRequired
};

function DetailList ({ event }) {
  const intl = useIntl();
  const actor = event.actor;
  const resource = event.resource;
  const entries = [
    ['timestamp', event.timestamp && <FormattedDateTime value={event.timestamp} withTime/>],
    ['action', event.action && actionLabel(intl, event.action)],
    ['category', event.action?.category && translateOrDefault(intl, `siem.events.category.${event.action.category}`, event.action.category)],
    ['user', <WithSecondary key='user' value={userLabel(intl, actor)} secondary={[actor?.email, actor?.userId]}/>],
    ['userType', [actor?.userType, actor?.authType].filter(Boolean).join(' / ')],
    ['ip', event.sourceIp],
    ['resource', resource && <WithSecondary key='resource' value={resourceLabel(intl, resource)} secondary={[resource.name && resource.id]}/>],
    ['status', [statusLabel(intl, event), event.statusCode].filter(Boolean).join(' · ')],
    ['failureReason', event.failureReason]
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');

  return (
    <dl className='detailList'>
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt><FormattedMessage id={`siem.events.detail.${key}`}/></dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
      {Object.entries(event.details || {}).map(([key, value]) => (
        <React.Fragment key={'details.' + key}>
          <dt>{translateOrDefault(intl, `siem.events.detail.${key}`, key)}</dt>
          <dd>
            {key === 'valueUpdates' && Array.isArray(value)
              ? <ValueChanges updates={value}/>
              : typeof value === 'object' ? <pre>{JSON.stringify(value, null, 2)}</pre> : String(value)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

DetailList.propTypes = {
  event: PropTypes.object.isRequired
};

/**
 * Expanded part of a row. Loads the complete stored item (raw) once and caches it in detailCache.
 */
function EventDetails ({ event, tenantId, detailCache }) {
  const [tab, setTab] = React.useState('overview');
  const [detail, setDetail] = React.useState(detailCache.current[event.timestampAndId]);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (detail) return;
    let active = true;
    loadSiemEvent(tenantId, event.timestampAndId)
      .then(loaded => {
        detailCache.current[event.timestampAndId] = loaded;
        if (active) setDetail(loaded);
      })
      .catch(() => active && setError(true));
    return () => { active = false; };
  }, [tenantId, event.timestampAndId, detail, detailCache]);

  return (
    <div className='eventDetails'>
      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        <Tab value='overview' label={<FormattedMessage id='siem.events.tab.overview'/>}/>
        <Tab value='raw' label={<FormattedMessage id='siem.events.tab.raw'/>}/>
      </Tabs>
      {tab === 'overview' && <DetailList event={detail || event}/>}
      {tab === 'raw' && (
        <>
          {!detail && !error && <CircularProgress size={24}/>}
          {error && <div className='error'><FormattedMessage id='siem.events.detail.error'/></div>}
          {detail &&
            <Code code={JSON.stringify(detail.raw || detail, null, 2)} language='json' plugins={['line-numbers']} embed={false}/>
          }
        </>
      )}
    </div>
  );
}

EventDetails.propTypes = {
  event: PropTypes.object.isRequired,
  tenantId: PropTypes.string,
  detailCache: PropTypes.object.isRequired
};

function EventRow ({ event, tenantId, detailCache }) {
  const intl = useIntl();
  const [open, setOpen] = React.useState(false);
  const classNames = [open && 'open', event.status === 'FAILURE' && 'failed'].filter(Boolean).join(' ');

  return (
    <>
      <TableRow hover className={classNames} onClick={() => setOpen(!open)}>
        <TableCell padding='checkbox'>
          <IconButton size='small' aria-label={intl.formatMessage({ id: 'siem.events.expand' })}>
            {open ? <KeyboardArrowDown/> : <KeyboardArrowRight/>}
          </IconButton>
        </TableCell>
        <TableCell className='nowrap'>
          {event.timestamp ? <FormattedDateTime value={event.timestamp} withTime/> : '–'}
        </TableCell>
        <TableCell className='user'>
          {userLabel(intl, event.actor)}
          {event.sourceIp && <div className='secondary'>{event.sourceIp}</div>}
        </TableCell>
        <TableCell>
          <span className='event'><StatusIcon event={event}/>{actionLabel(intl, event.action)}</span>
        </TableCell>
        <TableCell>{resourceLabel(intl, event.resource)}</TableCell>
        <TableCell className='details'>
          {detailSummary(event).map((line, index) => <div key={index}>{line}</div>)}
        </TableCell>
      </TableRow>
      <TableRow className='detailRow'>
        <TableCell colSpan={COLUMN_COUNT}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <EventDetails event={event} tenantId={tenantId} detailCache={detailCache}/>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

EventRow.propTypes = {
  event: PropTypes.object.isRequired,
  tenantId: PropTypes.string,
  detailCache: PropTypes.object.isRequired
};

/**
 * Protocol of the SIEM events of the tenant (GET /siem-events) with filters and cursor based "load more".
 * User, IP address and affected record are found through the free text search, so they have no filters of their own.
 */
export default function SiemEventLog ({ tenantId }) {
  const intl = useIntl();
  const [filter, setFilter] = React.useState(initialFilter);
  const [dateRange, setDateRange] = React.useState();
  const [searchInput, setSearchInput] = React.useState('');
  const [filterOptions, setFilterOptions] = React.useState({ actions: [] });
  const [events, setEvents] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState();
  const [truncated, setTruncated] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const requestCounter = React.useRef(0);
  const detailCache = React.useRef({});
  const moreRef = React.useRef();

  // The first load waits for the options, because the default event selection is derived from them.
  React.useEffect(() => {
    const applyOptions = (options) => {
      setFilterOptions(options);
      setFilter(current => current.actions === null ? { ...current, actions: defaultActions(options.actions) } : current);
    };
    loadSiemEventFilterOptions(tenantId)
      .then(options => applyOptions({ actions: options?.actions || [] }))
      .catch(() => applyOptions({ actions: [] }));
  }, [tenantId]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setFilter(current => current.q === searchInput ? current : { ...current, q: searchInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const load = React.useCallback(async (cursor) => {
    if (filter.actions === null) return;
    // Responses of outdated requests (filter changed meanwhile) are ignored.
    const request = ++requestCounter.current;
    setLoading(true);
    setError(false);
    try {
      // Selective filters often match only a few of the events read per request, so keep loading until the page is
      // full or MAX_AUTO_LOAD_ROUNDS is reached. Results are shown after each round.
      let found = 0;
      let next = cursor;
      for (let round = 0; round <= MAX_AUTO_LOAD_ROUNDS; round++) {
        const append = round > 0 || !!cursor;
        // Always the full page size: the server derives its DynamoDB batch size from the limit.
        const page = await loadSiemEvents(tenantId, { ...filter, limit: PAGE_SIZE, cursor: next });
        if (request !== requestCounter.current) return;
        const items = page?.items || [];
        found += items.length;
        next = page?.nextCursor;
        setEvents(current => append ? [...current, ...items] : items);
        setNextCursor(next);
        setTruncated(!!page?.truncated);
        if (!page?.truncated || !next || found >= PAGE_SIZE) break;
      }
    } catch (e) {
      if (request === requestCounter.current) setError(true);
    } finally {
      if (request === requestCounter.current) setLoading(false);
    }
  }, [tenantId, filter]);

  // Infinite scrolling: the next page is loaded as soon as the end of the list becomes visible. Not after a truncated
  // load, that one already used its read budget and continues only through the button.
  React.useEffect(() => {
    const element = moreRef.current;
    if (!element || !nextCursor || loading || error || truncated || !window.IntersectionObserver) return;
    const observer = new window.IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) load(nextCursor);
    }, { rootMargin: SCROLL_LOAD_MARGIN });
    observer.observe(element);
    return () => observer.disconnect();
  }, [nextCursor, loading, error, truncated, load]);

  React.useEffect(() => {
    load();
  }, [load]);

  const updateFilter = (changes) => setFilter(current => ({ ...current, ...changes }));

  const handleDateRange = (from, to, interval, range) => {
    setDateRange(range);
    updateFilter({ from, to });
  };

  const resetFilter = () => {
    setSearchInput('');
    setDateRange({ selectedIndex: DEFAULT_RANGE_INDEX, periodDisabled: true });
    setFilter(initialFilter(defaultActions(filterOptions.actions)));
  };

  const selectedActions = filter.actions || [];
  // "Reset" is only offered when something differs from the default filter.
  const filtered = !sameActions(selectedActions, defaultActions(filterOptions.actions)) || !!filter.status || !!searchInput ||
    (dateRange !== undefined && dateRange.selectedIndex !== DEFAULT_RANGE_INDEX);

  const actionsByCategory = filterOptions.actions.reduce((groups, option) => {
    (groups[option.category] = groups[option.category] || []).push(option);
    return groups;
  }, {});
  const categoryLabel = (category) => translateOrDefault(intl, `siem.events.category.${category}`, category);

  // Completely selected categories are shown by their name instead of listing all of their actions.
  const renderActions = (selected) => {
    if (!selected.length || selected.length === filterOptions.actions.length) {
      return intl.formatMessage({ id: 'siem.events.filter.allActions' });
    }
    const complete = Object.keys(actionsByCategory)
      .filter(category => actionsByCategory[category].every(option => selected.includes(option.code)));
    const single = selected.filter(code => !complete.includes(filterOptions.actions.find(option => option.code === code)?.category));
    return [...complete.map(categoryLabel), ...single.map(code => actionLabel(intl, { code }))].join(', ');
  };

  return (
    <div className='siemEventLog'>
      <h2>
        <FormattedMessage id='siem.events.title'/>
        <div><FormattedMessage id='siem.events.subtitle'/></div>
      </h2>

      <div className='filters'>
        <div className='filter period'>
          <DateRangePicker
            callback={handleDateRange}
            dateRange={dateRange}
            startIndex={DEFAULT_RANGE_INDEX}
            excludedOptions={EXCLUDED_RANGES}
            label={<FormattedMessage id='siem.events.filter.period'/>}
            fullWidth
          />
        </div>
        <TextField
          className='filter'
          size='small'
          select
          label={<FormattedMessage id='siem.events.filter.action'/>}
          value={selectedActions}
          onChange={(event) => updateFilter({ actions: toggleActions(selectedActions, event.target.value, actionsByCategory) })}
          SelectProps={{ multiple: true, displayEmpty: true, renderValue: renderActions }}
          InputLabelProps={{ shrink: true }}
        >
          {Object.entries(actionsByCategory).flatMap(([category, options]) => {
            const count = options.filter(option => selectedActions.includes(option.code)).length;
            return [
              <MenuItem key={CATEGORY_PREFIX + category} value={CATEGORY_PREFIX + category} className='categoryItem'>
                <Checkbox size='small' checked={count === options.length} indeterminate={count > 0 && count < options.length}/>
                <ListItemText primary={categoryLabel(category)}/>
              </MenuItem>,
              ...options.map(option => (
                <MenuItem key={option.code} value={option.code} className='actionItem'>
                  <Checkbox size='small' checked={selectedActions.includes(option.code)}/>
                  <ListItemText primary={actionLabel(intl, option)}/>
                </MenuItem>
              ))
            ];
          })}
        </TextField>
        <TextField
          className='filter search'
          size='small'
          label={<FormattedMessage id='search'/>}
          placeholder={intl.formatMessage({ id: 'siem.events.filter.searchPlaceholder' })}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{ startAdornment: <InputAdornment position='start'><Search fontSize='small'/></InputAdornment> }}
        />
        <FormControlLabel
          className='failedOnly'
          control={
            <Switch
              checked={filter.status === 'FAILURE'}
              onChange={(event) => updateFilter({ status: event.target.checked ? 'FAILURE' : '' })}
            />
          }
          label={<FormattedMessage id='siem.events.filter.failedOnly'/>}
        />
        {filtered &&
          <Button className='reset' startIcon={<RestartAlt/>} onClick={resetFilter}>
            <FormattedMessage id='siem.events.filter.reset'/>
          </Button>
        }
      </div>

      <div className='tableContainer'>
        {loading && <LinearProgress className='loadingBar'/>}
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'/>
              <TableCell><FormattedMessage id='siem.events.column.timestamp'/></TableCell>
              <TableCell><FormattedMessage id='siem.events.column.user'/></TableCell>
              <TableCell><FormattedMessage id='siem.events.column.action'/></TableCell>
              <TableCell><FormattedMessage id='siem.events.column.resource'/></TableCell>
              <TableCell><FormattedMessage id='siem.events.column.details'/></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(event => (
              <EventRow key={event.timestampAndId} event={event} tenantId={tenantId} detailCache={detailCache}/>
            ))}
          </TableBody>
        </Table>
      </div>

      {!loading && !error && events.length === 0 && !nextCursor &&
        <div className='hint empty'><FormattedMessage id='siem.events.empty'/></div>
      }
      {error &&
        <div className='hint error'>
          <FormattedMessage id='siem.events.error'/>
          <Button onClick={() => load(events.length ? nextCursor : undefined)}><FormattedMessage id='siem.events.retry'/></Button>
        </div>
      }
      {!error && nextCursor &&
        <div className='hint more' ref={moreRef}>
          {truncated && <span><FormattedMessage id='siem.events.truncated'/></span>}
          <Button variant='outlined' disabled={loading} onClick={() => load(nextCursor)}>
            <FormattedMessage id='siem.events.loadMore'/>
          </Button>
        </div>
      }
    </div>
  );
}

SiemEventLog.propTypes = {
  tenantId: PropTypes.string
};
